import React, { useMemo } from 'react';
import { Box, Switch, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import LinkIcon from '@mui/icons-material/Link';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StyledDataGrid from '../../../../components/DataGrid/DataGridStyles';
import { CustomAutocomplete } from '../../../Infra/CustomAutocomplete/CustomAutocomplete';
import { useLinkAccountsStep } from './useLinkAccountsStep';
import { LinkAccountsStepProps, UseLinkAccountsStepReturn } from './types';
import { StepMetadata } from '../../../../pages/DataFeedWizard/types';
import { getNumericStringSortComparator } from '../../../../utils/sortComparators';
import styles from './LinkAccountsStep.module.scss';

export const LinkAccountsStep: React.FC<LinkAccountsStepProps> = props => {
  const {
    rows,
    custodianOptions,
    getAvailableOptionsForRow,
    handleCustodianChange,
    handleUploadToggle,
  } = useLinkAccountsStep();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'accountNumber',
        headerName: 'Account Number',
        width: 200,
        sortable: true,
      },
      {
        field: 'custodianName',
        headerName: 'Custodian Name',
        flex: 1,
        sortable: true,
        // Sort by custodianName, empty values at bottom
        valueGetter: (_value: string | null | undefined, row: UseLinkAccountsStepReturn['rows'][number]) => row.custodianName || '',
        getSortComparator: getNumericStringSortComparator,
        renderCell: params => {
          const isDisabled = params.row.uploadTransactionsEnabled === false;
          // Match by both portfolioId AND custodianId for unique identification
          const selectedOption =
            custodianOptions.find(
              opt =>
                opt.custodianId === params.row.custodianId &&
                opt.portfolioId === params.row.portfolioId
            ) || null;

          return (
            <CustomAutocomplete
              disabled={isDisabled}
              value={selectedOption}
              onChange={(_, value) =>
                handleCustodianChange(
                  params.row.id,
                  value?.custodianId || null,
                  value?.portfolioId || null
                )
              }
              options={getAvailableOptionsForRow(params.row.id)}
              isOptionEqualToValue={(option, value) =>
                option.custodianId === value.custodianId &&
                option.portfolioId === value.portfolioId
              }
              getOptionLabel={option => {
                const clientPart = option.clientName || '';
                const portfolioPart = option.portfolioName || '';
                const custodianPart = option.custodianName || '';
                return `${clientPart} | ${portfolioPart} | ${custodianPart}`;
              }}
              placeholder="Select custodian"
              className={styles.custodianAutocomplete}
            />
          );
        },
      },
      {
        field: 'uploadTransactionsEnabled',
        headerName: 'Upload Transactions',
        width: 185,
        sortable: false,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>Upload Transactions</span>
            <Tooltip title="Exclude transactions for this account">
              <HelpOutlineIcon
                fontSize="small"
                sx={{ color: 'text.secondary', cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        ),
        renderCell: params => (
          <Switch
            checked={params.row.uploadTransactionsEnabled}
            onChange={e => handleUploadToggle(params.row.id, e.target.checked)}
            size="small"
          />
        ),
      },
    ],
    [
      custodianOptions,
      getAvailableOptionsForRow,
      handleCustodianChange,
      handleUploadToggle,
    ]
  );

  return (
    <Box className={styles.container}>
      <Box className={styles.section}>
        <Box className={styles.gridContainer}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            getRowId={row => row.id}
            density="standard"
            showCellVerticalBorder
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            className={styles.dataGrid}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const stepMetadata: StepMetadata = {
  title: 'Link Accounts to Custodians',
  subtitle: 'Map uploaded account numbers to existing custodians in the system',
  icon: <LinkIcon />,
  buttonText: 'Link Accounts',
};
